<template>
  <v-card class="elevation-12" :loading="loading || form.isParsing">
    <v-card-title>{{title}}</v-card-title>
    <v-card-text>
      <v-row>
        <v-col>
          <v-file-input label="Реестр доверенности для синхронизации" @change="onFileChange"/>
        </v-col>
        <v-col>
          <template v-if="form.sheet">
            <v-text-field v-model="form.skipRows" type="number" min="0" label="Игнорировать верхние строки"
                          readonly
                          append-outer-icon="add" @click:append-outer="incrementSkipRows"
                          prepend-icon="remove" @click:prepend="decrementSkipRows"
                          style="max-width: 300px"
            />
          </template>
        </v-col>
        <v-col>
          <template v-if="form.sheet">
            <p class="d-flex">
                            <span v-if="$v.columnIndexes.$invalid" class="red--text">
                                Выберите нужных колонок для распознования данных реестра
                            </span>

              <v-spacer/>
              <v-btn color="primary" @click="onImportButtonClicked"
                     :disabled="$v.$invalid" text>
                Синхронизировать
              </v-btn>
            </p>
          </template>
        </v-col>
      </v-row>

      <h3 v-if="form.isParsingError" class="red--text text-center">Ошибка при чтении файла</h3>
      <h3 v-if="importingError" class="red--text text-center">Ошибка при синхронизации</h3>
      <template v-if="showCounts">
        <h3 v-if="successCount" class="text-center green--text">Успешно обновлено: {{ successCount }}</h3>
        <h3 v-if="errorCount" class="text-center red--text">Не удалось обновить: {{ errorCount }}</h3>
      </template>

      <template v-if="form.sheet">
        <hr class="my-2"/>
        <template v-if="loading">
          <v-progress-linear
              v-model="progress"
              color="light-blue"
              height="25"
              reactive
          >
            <strong>{{ Math.ceil(progress) }}%</strong>
          </v-progress-linear>
        </template>
        <v-row>
          <v-col cols="12">
            <v-data-table
                :headers="form.fields"
                :items="form.sheet"
                class="elevation-10"
                dense
                :items-per-page="10"
                :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
            >

              <template v-for="field in form.fields" :slot="`header.${field.value}`">
                <v-select v-model="field.type" :key="field.value"
                          :items="form.columnDropdownOptions"
                          label="Тип колонки"
                          @change="onFieldColumnTypeChanged(field)"
                />
              </template>

              <template v-slot:item="scope">
                <tr :class="{'blue-grey darken-1': scope.item._rowNumber <= form.skipRows}">
                  <td v-for="field in form.fields" :key="field.value">{{ scope.item[field.value] }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </template>

      <template v-if="badRequestDataList.length > 0">
        <hr class="my-2"/>
        <h2>Не удалось загрузить</h2>
        <v-row>
          <v-col cols="12">
            <v-data-table
                :headers="headers"
                :items="badRequestDataList"
                dense
                :items-per-page="10"
                :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
            >
              <template v-slot:item.Status="scope">
                <v-chip small color="red">{{scope.item.StatusText}}</v-chip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </template>
    </v-card-text>
  </v-card>
</template>
<script>
import {required} from 'vuelidate/lib/validators'
import {validationMixin} from 'vuelidate'

import _ from 'underscore'
import moment from 'moment'

import ParseService from '@/services/parse.service'
import AttorneyService from '@/services/attorney.service';

const UnimportantColumn = null;

const NumberColumn = 'NumberColumn';
const StartColumnIndex = 'StartColumnIndex';
const EndColumnIndex = 'EndColumnIndex';

const PositionColumnIndex = 'PositionColumnIndex';
const FullNameColumnIndex = 'FullNameColumnIndex';
const PersonInnColumnIndex = 'PersonInnColumnIndex';

const PassportSerialColumnIndex = 'PassportSerialColumnIndex';
const PassportNumberColumnIndex = 'PassportNumberColumnIndex';
const PassportIssuerColumnIndex = 'PassportIssuerColumnIndex';
const PassportIssuedOnColumnIndex = 'PassportIssuedOnColumnIndex';

const ContractorInnColumnIndex = 'ContractorInnColumnIndex';
const ContractorNameColumnIndex = 'ContractorNameColumnIndex';
const ContractNumberColumnIndex = 'ContractNumberColumnIndex';
const ContractDateColumnIndex = 'ContractDateColumnIndex';

const ItemsProductNamesColumn = 'ItemsProductNamesColumn';
const ItemsMeasurementColumn = 'ItemsMeasurementColumn';
const ItemsQuantityColumn = 'ItemsQuantityColumn';

const CompanyParamImportAttorneyProperties = 'ImportOrderRegistryProperties';

import CompanyParamsService from '@/services/company.param.service';

export default {
  name: 'SyncAttorneysForm',
  mixins: [validationMixin],
  props: ['title'],
  data() {
    return {
      progress: 0,
      headers: [
        {
          text: 'Ф.И.О',
          value: 'Fullname',
          sortable: false
        },
        {
          text: 'Наименования контрагента',
          sortable: false,
          value: 'ContractorName'
        },
        {
          text: 'Номер контракта',
          sortable: false,
          value: 'ContractNumber'
        },
        {
          text: 'Должность',
          sortable: false,
          value: 'Position'
        },
        {
          text: 'Номер доверенность',
          sortable: false,
          value: 'Number'
        },
        {
          text: 'Инн сотрудника',
          sortable: false,
          value: 'PersonInn'
        },
        {
          text: 'Дата начала',
          sortable: false,
          value: 'Start'
        },
        {
          text: 'Дата окончания',
          sortable: false,
          value: 'End'
        },
        {
          text: 'Серийный паспорт',
          sortable: false,
          value: 'PassportSerial'
        },
        {
          text: 'Номер паспорта',
          sortable: false,
          value: 'PassportNumber'
        },
        {
          text: 'Паспорт кем выдан',
          sortable: false,
          value: 'PassportIssuer'
        },
        {
          text: 'Паспорт дата выдано',
          sortable: false,
          value: 'PassportIssuedOn'
        },
        {
          text: 'Контрагент Инн',
          sortable: false,
          value: 'ContractorInn'
        },
        {
          text: 'Дата контракта',
          sortable: false,
          value: 'ContractDate'
        },
        {
          text: 'Ошибка',
          sortable: false,
          value: 'Status'
        },
      ],
      loading: false,
      importingError: false,
      successCount: 0,
      errorCount: 0,
      showCounts: false,
      badRequestDataList: [],
      form: {
        file: null,
        skipRows: 0,

        fields: [],
        isParsing: false,
        isParsingError: false,
        sheet: null,

        columnDropdownOptions: [
          {text: 'Неважно', value: UnimportantColumn},

          {text: 'Довереность номер', value: NumberColumn},
          {text: 'Довереность дата', value: StartColumnIndex},
          {text: 'Довереность дата действия', value: EndColumnIndex},

          {text: 'Должность', value: PositionColumnIndex},
          {text: 'Ф.И.О', value: FullNameColumnIndex},
          {text: 'Инн сотрудника', value: PersonInnColumnIndex},

          {text: 'Пасспорт серия', value: PassportSerialColumnIndex},
          {text: 'Номер пасспорта', value: PassportNumberColumnIndex},
          {text: 'Пасспорт кем выдан', value: PassportIssuerColumnIndex},
          {text: 'Дата выдачи пасспорта', value: PassportIssuedOnColumnIndex},

          {text: 'Инн контерагента', value: ContractorInnColumnIndex},
          {text: 'Наименования контерагента', value: ContractorNameColumnIndex},
          {text: 'Номер договора', value: ContractNumberColumnIndex},
          {text: 'Дата договора', value: ContractDateColumnIndex},

          {text: 'Названия продукта', value: ItemsProductNamesColumn},
          {text: 'Ед.Изм. продукта', value: ItemsMeasurementColumn},
          {text: 'Количества продукта', value: ItemsQuantityColumn},
        ]
      },
      columnIndexes: {
        numberColumnIndex: null,
        startColumnIndex: null,
        endColumnIndex: null,

        positionColumnIndex: null,
        fullNameColumnIndex: null,
        personInnColumnIndex: null,

        passportSerialColumnIndex: null,
        passportNumberColumnIndex: null,
        passportIssuerColumnIndex: null,
        passportIssuedOnColumnIndex: null,

        contractorInnColumnIndex: null,
        contractorNameColumnIndex: null,
        contractNumberColumnIndex: null,
        contractDateColumnIndex: null,

        itemsProductNamesColumn: null,
        itemsMeasurementColumn: null,
        itemsQuantityColumn: null,
      }
    }
  },

  validations: {
    form: {
      file: {
        required
      }
    },
    columnIndexes: {
      numberColumnIndex: {required},
      startColumnIndex: {required},
      endColumnIndex: {required},

      positionColumnIndex: {required},
      fullNameColumnIndex: {required},
      personInnColumnIndex: {required},

      passportSerialColumnIndex: {required},
      passportNumberColumnIndex: {required},
      passportIssuerColumnIndex: {required},
      passportIssuedOnColumnIndex: {required},

      contractorInnColumnIndex: {required},
      contractorNameColumnIndex: {required},
      contractNumberColumnIndex: {required},

      itemsProductNamesColumn: {required},
      itemsMeasurementColumn: {required},
      itemsQuantityColumn: {required},
    }
  },
  methods: {
    onFieldColumnTypeChanged(field) {
      if (field.type === NumberColumn
          || field.type === StartColumnIndex
          || field.type === StartColumnIndex
          || field.type === EndColumnIndex

          || field.type === PositionColumnIndex
          || field.type === FullNameColumnIndex
          || field.type === PersonInnColumnIndex

          || field.type === PassportSerialColumnIndex
          || field.type === PassportNumberColumnIndex
          || field.type === PassportIssuerColumnIndex
          || field.type === PassportIssuedOnColumnIndex

          || field.type === ContractorInnColumnIndex
          || field.type === ContractorNameColumnIndex
          || field.type === ContractNumberColumnIndex
          || field.type === ContractDateColumnIndex

          || field.type === ItemsProductNamesColumn
          || field.type === ItemsMeasurementColumn
          || field.type === ItemsQuantityColumn) {
        _.each(this.form.fields, iteratorField => {
          if (iteratorField.type === field.type && iteratorField !== field)
            iteratorField.type = UnimportantColumn;
        });
      }

      this.resetColumnIndexers();
    },
    resetColumnIndexers() {
      this.columnIndexes.numberColumnIndex = null;
      this.columnIndexes.startColumnIndex = null;
      this.columnIndexes.endColumnIndex = null;

      this.columnIndexes.positionColumnIndex = null;
      this.columnIndexes.fullNameColumnIndex = null;
      this.columnIndexes.personInnColumnIndex = null;

      this.columnIndexes.passportSerialColumnIndex = null;
      this.columnIndexes.passportNumberColumnIndex = null;
      this.columnIndexes.passportIssuerColumnIndex = null;
      this.columnIndexes.passportIssuedOnColumnIndex = null;

      this.columnIndexes.contractorInnColumnIndex = null;
      this.columnIndexes.contractorNameColumnIndex = null;
      this.columnIndexes.contractNumberColumnIndex = null;
      this.columnIndexes.contractDateColumnIndex = null;

      this.columnIndexes.itemsProductNamesColumn = null;
      this.columnIndexes.itemsMeasurementColumn = null;
      this.columnIndexes.itemsQuantityColumn = null;

      const vm = this;

      _.each(vm.form.fields, field => {
        if (field.type === NumberColumn)
          this.columnIndexes.numberColumnIndex = field.value;
        else if (field.type === StartColumnIndex)
          this.columnIndexes.startColumnIndex = field.value;
        else if (field.type === EndColumnIndex)
          this.columnIndexes.endColumnIndex = field.value;

        else if (field.type === PositionColumnIndex)
          this.columnIndexes.positionColumnIndex = field.value;
        else if (field.type === FullNameColumnIndex)
          this.columnIndexes.fullNameColumnIndex = field.value;
        else if (field.type === PersonInnColumnIndex)
          this.columnIndexes.personInnColumnIndex = field.value;

        else if (field.type === PassportSerialColumnIndex)
          this.columnIndexes.passportSerialColumnIndex = field.value;
        else if (field.type === PassportNumberColumnIndex)
          this.columnIndexes.passportNumberColumnIndex = field.value;
        else if (field.type === PassportIssuerColumnIndex)
          this.columnIndexes.passportIssuerColumnIndex = field.value;
        else if (field.type === PassportIssuedOnColumnIndex)
          this.columnIndexes.passportIssuedOnColumnIndex = field.value;

        else if (field.type === ContractorInnColumnIndex)
          this.columnIndexes.contractorInnColumnIndex = field.value;
        else if (field.type === ContractorNameColumnIndex)
          this.columnIndexes.contractorNameColumnIndex = field.value;
        else if (field.type === ContractNumberColumnIndex)
          this.columnIndexes.contractNumberColumnIndex = field.value;
        else if (field.type === ContractDateColumnIndex)
          this.columnIndexes.contractDateColumnIndex = field.value;

        else if (field.type === ItemsProductNamesColumn)
          this.columnIndexes.itemsProductNamesColumn = field.value;
        else if (field.type === ItemsMeasurementColumn)
          this.columnIndexes.itemsMeasurementColumn = field.value;
        else if (field.type === ItemsQuantityColumn)
          this.columnIndexes.itemsQuantityColumn = field.value;
      });
    },

    incrementSkipRows() {
      this.form.skipRows = parseInt(this.form.skipRows, 10) + 1;
    },
    decrementSkipRows() {
      this.form.skipRows = parseInt(this.form.skipRows, 10) - 1;
      this.form.skipRows = this.form.skipRows >= 0 ? this.form.skipRows : 0;
    },

    onFileChange(file) {
      this.form.file = file;
      this.showCounts = false;

      if (this.form.file)
        this.parseFile();
      else
        this.clearFile();
    },

    parseFile() {
      const vm = this;

      vm.form.isParsing = true;
      vm.form.isParsingError = false;

      vm.form.fields = [];
      vm.form.sheet = null;
      vm.form.skipRows = 0;

      let reader = new FileReader();

      reader.onload = function (e) {
        const base64Content = e.target.result.split(',')[1];
        ParseService.parse(base64Content)
            .then(response => {
              if (response.data.length) {

                vm.form.sheet = _.map(response.data[0].Data, (row, index) => {

                  const object = {};
                  let rowKeys = Object.keys(row);

                  // convert array index => value into key => value object
                  _.each(rowKeys, rowKey => {
                    object[rowKey] = row[rowKey];
                  });
                  object._rowNumber = index + 1;
                  // collect using fields
                  if (rowKeys.length > vm.form.fields.length)
                    vm.form.fields = _.map(rowKeys, rowKey => {
                      let fieldTypeValue = null;
                      switch (rowKey) {
                        case vm.columnIndexes.numberColumnIndex:
                          fieldTypeValue = NumberColumn;
                          break;
                        case vm.columnIndexes.startColumnIndex:
                          fieldTypeValue = StartColumnIndex;
                          break;
                        case vm.columnIndexes.endColumnIndex:
                          fieldTypeValue = EndColumnIndex;
                          break;
                        case vm.columnIndexes.positionColumnIndex:
                          fieldTypeValue = PositionColumnIndex;
                          break;
                        case vm.columnIndexes.fullNameColumnIndex:
                          fieldTypeValue = FullNameColumnIndex;
                          break;
                        case vm.columnIndexes.personInnColumnIndex:
                          fieldTypeValue = PersonInnColumnIndex;
                          break;
                        case vm.columnIndexes.passportSerialColumnIndex:
                          fieldTypeValue = PassportSerialColumnIndex;
                          break;
                        case vm.columnIndexes.passportNumberColumnIndex:
                          fieldTypeValue = PassportNumberColumnIndex;
                          break;
                        case vm.columnIndexes.passportIssuerColumnIndex:
                          fieldTypeValue = PassportIssuerColumnIndex;
                          break;
                        case vm.columnIndexes.passportIssuedOnColumnIndex:
                          fieldTypeValue = PassportIssuedOnColumnIndex;
                          break;
                        case vm.columnIndexes.contractorInnColumnIndex:
                          fieldTypeValue = ContractorInnColumnIndex;
                          break;
                        case vm.columnIndexes.contractorNameColumnIndex:
                          fieldTypeValue = ContractorNameColumnIndex;
                          break;
                        case vm.columnIndexes.contractNumberColumnIndex:
                          fieldTypeValue = ContractNumberColumnIndex;
                          break;
                        case vm.columnIndexes.contractDateColumnIndex:
                          fieldTypeValue = ContractDateColumnIndex;
                          break;
                        case vm.columnIndexes.itemsProductNamesColumn:
                          fieldTypeValue = ItemsProductNamesColumn;
                          break;
                        case vm.columnIndexes.itemsMeasurementColumn:
                          fieldTypeValue = ItemsMeasurementColumn;
                          break;
                        case vm.columnIndexes.itemsQuantityColumn:
                          fieldTypeValue = ItemsQuantityColumn;
                          break;
                      }

                      return {
                        type: fieldTypeValue,
                        text: rowKey,
                        sortable: false,
                        value: rowKey,
                      }
                    });

                  return object;
                });
              }
            })
            .catch(() => {
              vm.form.isParsingError = true;
            })
            .finally(() => {
              vm.form.isParsing = false;
              vm.loading = false;
            });
      };

      reader.onerror = function () {
        vm.form.isParsing = false;
        vm.form.isParsingError = true;
        vm.loading = false;
      };
      vm.loading = true;
      reader.readAsDataURL(vm.form.file);
    },

    clearFile() {
      this.form.fields = [];
      this.form.sheet = null;
      this.form.skipRows = 0;
    },

    cashedColumns() {
      let vm = this;
      CompanyParamsService.save(CompanyParamImportAttorneyProperties, JSON.stringify({
        numberColumnIndex: vm.columnIndexes.numberColumnIndex,
        startColumnIndex: vm.columnIndexes.startColumnIndex,
        endColumnIndex: vm.columnIndexes.endColumnIndex,

        positionColumnIndex: vm.columnIndexes.positionColumnIndex,
        fullNameColumnIndex: vm.columnIndexes.fullNameColumnIndex,
        personInnColumnIndex: vm.columnIndexes.personInnColumnIndex,

        passportSerialColumnIndex: vm.columnIndexes.passportSerialColumnIndex,
        passportNumberColumnIndex: vm.columnIndexes.passportNumberColumnIndex,
        passportIssuerColumnIndex: vm.columnIndexes.passportIssuerColumnIndex,
        passportIssuedOnColumnIndex: vm.columnIndexes.passportIssuedOnColumnIndex,

        contractorInnColumnIndex: vm.columnIndexes.contractorInnColumnIndex,
        contractorNameColumnIndex: vm.columnIndexes.contractorNameColumnIndex,
        contractNumberColumnIndex: vm.columnIndexes.contractNumberColumnIndex,
        contractDateColumnIndex: vm.columnIndexes.contractDateColumnIndex,

        itemsProductNamesColumn: vm.columnIndexes.itemsProductNamesColumn,
        itemsMeasurementColumn: vm.columnIndexes.itemsMeasurementColumn,
        itemsQuantityColumn: vm.columnIndexes.itemsQuantityColumn,
      })).finally(() => {
        vm.form.file = null;
        vm.form.sheet = null;
        vm.form.fields = [];

        vm.$emit("imported");
      });
    },

    runImportAttorneys(attorneys, runIndex) {
      let vm = this;

      if (runIndex < attorneys.length) {

        AttorneyService.createAttorney(attorneys[runIndex], true)
            .then((response) => {

              vm.progress = Math.ceil(runIndex / attorneys.length * 100);

              if (response && response.Status === 400) {
                vm.badRequestDataList.push(response);
              }
            }, (error) => {
              vm.importingError = true;
            })
            .finally(() => {
              vm.runImportAttorneys(attorneys, runIndex + 1);
            });
      } else {
        vm.cashedColumns();
        this.loading = false;
        vm.$emit('imported');
        // hide loader
      }
    },

    onImportButtonClicked() {
      const vm = this;

      vm.loading = true;
      const attorneys = [];

      for (let i = vm.form.skipRows; i < vm.form.sheet.length; i++) {
        const attorneyNumber = vm.form.sheet[i][vm.columnIndexes.numberColumnIndex];

        let contractDate = null;
        if (typeof (vm.columnIndexes.contractDateColumnIndex) === 'string')
          contractDate = moment(vm.form.sheet[i][vm.columnIndexes.contractDateColumnIndex], 'DD.MM.YYYY');

        let passportNumber = null;

        if (vm.form.sheet[i][vm.columnIndexes.passportNumberColumnIndex]) {
          passportNumber = vm.form.sheet[i][vm.columnIndexes.passportNumberColumnIndex].toString();
          passportNumber = passportNumber.trim();
          //adding zeros to the beginning of a passport number(max length 7 chars)
          while (passportNumber.length < 7) {
            passportNumber = '0' + passportNumber;
          }
        } else {
          passportNumber = vm.form.sheet[i][vm.columnIndexes.passportNumberColumnIndex];
        }

        let product = vm.form.sheet[i][vm.columnIndexes.itemsProductNamesColumn];
        if (typeof (product) === 'string')
          product = product.trim();

        let measurement = vm.form.sheet[i][vm.columnIndexes.itemsMeasurementColumn];
        if (typeof (measurement) === 'string')
          measurement = measurement.trim();

        let quantity = vm.form.sheet[i][vm.columnIndexes.itemsQuantityColumn];
        if (typeof (quantity) === 'string')
          quantity = quantity.trim();

        const item = {
          product: product,
          measurement: measurement,
          quantity: quantity
        };
        let existingAttorney = _.find(attorneys, a => {
          return a.Number === attorneyNumber && a.Number !== "" && a.Number !== null;
        }) || null;

        if (existingAttorney) {
          let isExistItem = _.any(existingAttorney.Items, function (i) {
            return _.isEqual(i, item)
          });
          if (!isExistItem) {
            existingAttorney.Items.push(item);
          }
        } else {
          attorneys.push({
            Number: (typeof (attorneyNumber) === 'string') ? attorneyNumber.trim() : attorneyNumber,
            Start: moment(vm.form.sheet[i][vm.columnIndexes.startColumnIndex], 'DD.MM.YYYY'),
            End: moment(vm.form.sheet[i][vm.columnIndexes.endColumnIndex], 'DD.MM.YYYY'),

            Position: (typeof (vm.form.sheet[i][vm.columnIndexes.positionColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.positionColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.positionColumnIndex],
            Fullname: (typeof (vm.form.sheet[i][vm.columnIndexes.fullNameColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.fullNameColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.fullNameColumnIndex],
            PersonInn: (typeof (vm.form.sheet[i][vm.columnIndexes.personInnColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.personInnColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.personInnColumnIndex],

            PassportSerial: (typeof (vm.form.sheet[i][vm.columnIndexes.passportSerialColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.passportSerialColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.passportSerialColumnIndex],
            PassportNumber: passportNumber,
            PassportIssuer: (typeof (vm.form.sheet[i][vm.columnIndexes.passportIssuerColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.passportIssuerColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.passportIssuerColumnIndex],
            PassportIssuedOn: moment(vm.form.sheet[i][vm.columnIndexes.passportIssuedOnColumnIndex], 'DD.MM.YYYY'),

            ContractorInn: (typeof (vm.form.sheet[i][vm.columnIndexes.contractorInnColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.contractorInnColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.contractorInnColumnIndex],
            ContractorName: (typeof (vm.form.sheet[i][vm.columnIndexes.contractorNameColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.contractorNameColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.contractorNameColumnIndex],
            ContractNumber: (typeof (vm.form.sheet[i][vm.columnIndexes.contractNumberColumnIndex]) === 'string') ? vm.form.sheet[i][vm.columnIndexes.contractNumberColumnIndex].trim() : vm.form.sheet[i][vm.columnIndexes.contractNumberColumnIndex],
            ContractDate: contractDate,
            Items: [item]
          });
        }
      }
      vm.importingError = false;
      // run loader
      this.runImportAttorneys(attorneys, 0);
    },

    tryLoadPropertiesFromServer() {
      const vm = this;

      CompanyParamsService.get(CompanyParamImportAttorneyProperties)
          .then(response => {
            if (response.data) {
              const params = JSON.parse(response.data);

              vm.columnIndexes.numberColumnIndex = params.numberColumnIndex;
              vm.columnIndexes.startColumnIndex = params.startColumnIndex;
              vm.columnIndexes.endColumnIndex = params.endColumnIndex;

              vm.columnIndexes.positionColumnIndex = params.positionColumnIndex;
              vm.columnIndexes.fullNameColumnIndex = params.fullNameColumnIndex;
              vm.columnIndexes.personInnColumnIndex = params.personInnColumnIndex;

              vm.columnIndexes.passportSerialColumnIndex = params.passportSerialColumnIndex;
              vm.columnIndexes.passportNumberColumnIndex = params.passportNumberColumnIndex;
              vm.columnIndexes.passportIssuerColumnIndex = params.passportIssuerColumnIndex;
              vm.columnIndexes.passportIssuedOnColumnIndex = params.passportIssuedOnColumnIndex;

              vm.columnIndexes.contractorInnColumnIndex = params.contractorInnColumnIndex;
              vm.columnIndexes.contractorNameColumnIndex = params.contractorNameColumnIndex;
              vm.columnIndexes.contractNumberColumnIndex = params.contractNumberColumnIndex;
              vm.columnIndexes.contractDateColumnIndex = params.contractDateColumnIndex;

              vm.columnIndexes.itemsProductNamesColumn = params.itemsProductNamesColumn;
              vm.columnIndexes.itemsMeasurementColumn = params.itemsMeasurementColumn;
              vm.columnIndexes.itemsQuantityColumn = params.itemsQuantityColumn;
            }
          })
    }
  },
  created() {
    this.tryLoadPropertiesFromServer();
  }
}
</script>
